import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../../components/shared/Sidebar';
import { DashboardHeader } from '../../components/shared/DashboardHeader';
import { motion } from 'framer-motion';
import { CreditCard, Building, Smartphone, ArrowRight, ShieldCheck, Lock } from 'lucide-react';
import { formatCurrency } from '../../utils/calculations';
import { useToast } from '../../hooks/useToast';
export function PaymentOptions() {
  const navigate = useNavigate();
  const {
    toast
  } = useToast();
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const paymentMethods = [{
    id: 'card',
    title: 'Debit Card',
    description: 'Pay securely with Mastercard, Visa, or Verve',
    icon: CreditCard,
    color: 'blue'
  }, {
    id: 'transfer',
    title: 'Bank Transfer',
    description: 'Transfer directly to a dedicated virtual account',
    icon: Building,
    color: 'green'
  }, {
    id: 'ussd',
    title: 'USSD',
    description: "Pay using your bank's USSD code",
    icon: Smartphone,
    color: 'purple'
  }];
  const handlePayment = () => {
    if (!selectedMethod) {
      toast.error('Please select a payment method');
      return;
    }
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      navigate('/tenant/payment-success');
    }, 2000);
  };
  return <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors pb-16 lg:pb-0" data-id="element-4774">
      <Sidebar data-id="element-4775" />

      <main className="flex-1 lg:ml-[240px] transition-all duration-300" data-id="element-4776">
        <DashboardHeader data-id="element-4777" />

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8" data-id="element-4778">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-4779">
            <div className="text-center mb-8 lg:mb-10" data-id="element-4780">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-4781">
                Choose Payment Method
              </h1>
              <p className="text-gray-500 dark:text-gray-400" data-id="element-4782">
                Select how you would like to pay your rent
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-8 transition-colors" data-id="element-4783">
              <div className="p-6 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-700/50 flex justify-between items-center" data-id="element-4784">
                <span className="text-gray-600 dark:text-gray-400 font-medium" data-id="element-4785">
                  Total Amount
                </span>
                <span className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-4786">
                  {formatCurrency(150000)}
                </span>
              </div>

              <div className="p-6 space-y-4" data-id="element-4787">
                {paymentMethods.map(method => <button key={method.id} onClick={() => setSelectedMethod(method.id)} className={`w-full flex items-center p-4 rounded-xl border-2 transition-all duration-200 ${selectedMethod === method.id ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-500 dark:ring-blue-400' : 'border-gray-100 dark:border-gray-700 hover:border-gray-200 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`} data-id="element-4788">
                    <div className={`h-12 w-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0 ${selectedMethod === method.id ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`} data-id="element-4789">
                      <method.icon className="h-6 w-6" data-id="element-4790" />
                    </div>
                    <div className="text-left flex-1 min-w-0" data-id="element-4791">
                      <h3 className={`font-bold truncate ${selectedMethod === method.id ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`} data-id="element-4792">
                        {method.title}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2" data-id="element-4793">
                        {method.description}
                      </p>
                    </div>
                    <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedMethod === method.id ? 'border-blue-500 dark:border-blue-400' : 'border-gray-300 dark:border-gray-600'}`} data-id="element-4794">
                      {selectedMethod === method.id && <div className="h-3 w-3 rounded-full bg-blue-500 dark:bg-blue-400" data-id="element-4795" />}
                    </div>
                  </button>)}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-8" data-id="element-4796">
              <div className="flex items-center gap-1" data-id="element-4797">
                <Lock className="h-4 w-4 text-green-500 dark:text-green-400 flex-shrink-0" data-id="element-4798" />
                <span className="whitespace-nowrap" data-id="element-4799">
                  256-bit SSL Encryption
                </span>
              </div>
              <div className="flex items-center gap-1" data-id="element-4800">
                <ShieldCheck className="h-4 w-4 text-green-500 dark:text-green-400 flex-shrink-0" data-id="element-4801" />
                <span className="whitespace-nowrap" data-id="element-4802">Secure Payment</span>
              </div>
            </div>

            <button onClick={handlePayment} disabled={!selectedMethod || isProcessing} className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold text-lg shadow-lg hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center" data-id="element-4803">
              {isProcessing ? <span className="flex items-center gap-3" data-id="element-4804">
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-4805">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-4806"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-4807"></path>
                  </svg>
                  Processing Payment...
                </span> : <>
                  Pay {formatCurrency(150000)}
                  <ArrowRight className="ml-2 h-5 w-5 flex-shrink-0" data-id="element-4808" />
                </>}
            </button>
          </motion.div>
        </div>
      </main>
    </div>;
}